import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAssetsByIdQuery } from '@ports/internal-services/asset/assetSlice';
import { MediaInfoSidebarProps } from '@business/interfaces/photo';
import moment from 'moment';
import { dateYearFormatWithTime } from '@utils/constants/globals';
import { Button } from '@business/workflows/ComponentUI';
import { SVGAssets } from '@business/assets/vectors';
import EditTagsModal from './EditTagsModal';

const MediaInfoSidebar: React.FC<MediaInfoSidebarProps> = ({
  isOpen,
  onClose,
  onReopen,
  assetId
}) => {
  const { t } = useTranslation();
  const [isEditTagsModalOpen, setIsEditTagsModalOpen] = useState(false);
  console.log(onReopen);
  // Fetch asset data by ID
  const { data: assetDetailsData, refetch: refetchAsset } =
    useGetAssetsByIdQuery({ id: assetId });

  const assetData = assetDetailsData?.data;
  const mediaType = assetData?.type || 'image';

  // Format file size
  const formatSize = (bytes?: number) => {
    if (!bytes) return t('mediaInfoSidebar.unknown');
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };
  const fileSize = formatSize(assetData?.size);

  // Format dates
  const formatDate = (dateString?: string) => {
    if (!dateString) return t('mediaInfoSidebar.unknown');
    return moment(dateString).format(dateYearFormatWithTime);
  };

  const createdDate = formatDate(assetData?.createdAt);
  const modifiedDate = formatDate(assetData?.updatedAt);
  const lastOpenDate = formatDate(assetData?.assetViewDetails?.updatedAt);

  const handleOpenEditTags = () => {
    setIsEditTagsModalOpen(true);
  };

  const handleCloseEditTags = () => {
    setIsEditTagsModalOpen(false);
    refetchAsset(); // Refresh the data
  };

  const handleTagsUpdated = () => {
    // Refetch asset data to get updated tags
    refetchAsset();
  };

  const currentTags =
    assetData?.photoTags?.map((tag: any) => ({
      id: tag.id,
      tagName: tag.tagName
    })) || [];

  if (!isOpen && !isEditTagsModalOpen) return null;

  return (
    <>
      {/* Backdrop */}
      {isOpen && !isEditTagsModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-[60] transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      {isOpen && !isEditTagsModalOpen && (
        <div className="fixed right-0 top-0 h-full w-72 bg-white shadow-2xl z-[99] transform transition-transform duration-300 ease-in-out overflow-y-auto">
          {/* Content */}
          <div className="p-6">
            {/* Header with Close Button */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-1">
                  {t('mediaInfoSidebar.closerLook')}
                </h2>
                <p className="text-xs text-gray-500">
                  {t('mediaInfoSidebar.closerLookDescription')}
                </p>
              </div>
              <Button
                onClick={onClose}
                className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                btnStyle="no-outline">
                <SVGAssets.CloseBtn className="text-black" />
              </Button>
            </div>

            {/* Owner Section */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <SVGAssets.Owner className="w-5 h-5 text-gray-700" />
                <h3 className="text-sm font-semibold text-gray-900">
                  {t('mediaInfoSidebar.owner')}
                </h3>
              </div>
              <div className="flex items-center gap-3 ml-7">
                <div className="w-10 h-10 bg-gray-300  rounded-full flex items-center justify-center overflow-hidden">
                  {assetData?.user?.userDetail?.profileImage ? (
                    <img
                      src={assetData.user.userDetail.profileImage}
                      alt={assetData.user.fullName || 'Owner'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-sm font-semibold text-gray-600 uppercase">
                      {assetData?.user?.fullName?.charAt(0) || 'U'}
                    </span>
                  )}
                </div>
                <span className="text-sm text-gray-900">
                  {assetData?.user?.fullName || t('mediaInfoSidebar.unknown')}
                </span>
              </div>
            </div>

            {/* People Section */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <SVGAssets.PeopleIcon className="w-5 h-5 text-gray-700" />
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('mediaInfoSidebar.people')}
                  </h3>
                </div>
              </div>
              <div className="space-y-3 ml-7">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
                    <span className="text-sm font-semibold text-gray-600">
                      S
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-900">Sarah</p>
                    <p className="text-xs text-gray-500">
                      {t('mediaInfoSidebar.sister')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
                    <span className="text-sm font-semibold text-gray-600">
                      P
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-900">Peter</p>
                    <p className="text-xs text-gray-500">
                      {t('mediaInfoSidebar.brotherInLaw')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Section */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <SVGAssets.Locations className="w-5 h-5 text-gray-700" />
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('mediaInfoSidebar.location')}
                  </h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 ml-7">
                {assetData?.location || ''}
              </p>
            </div>

            {/* Last Modified Date */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t('mediaInfoSidebar.lastModifiedDate')}
              </h3>
              <p className="text-sm text-gray-600">{modifiedDate}</p>
            </div>

            {/* Last Open */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t('mediaInfoSidebar.lastOpen')}
              </h3>
              <p className="text-sm text-gray-600">{lastOpenDate}</p>
            </div>

            {/* Creation Date */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  {t('mediaInfoSidebar.creationDate')}
                </h3>
              </div>
              <p className="text-sm text-gray-600">{createdDate}</p>
            </div>

            {/* Type */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t('mediaInfoSidebar.type')}
              </h3>
              <p className="text-sm text-gray-600">
                {assetData?.type || mediaType || t('mediaInfoSidebar.unknown')}
              </p>
            </div>

            {/* Size */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t('mediaInfoSidebar.size')}
              </h3>
              <p className="text-sm text-gray-600">{fileSize}</p>
            </div>

            {/* Things (Tags) */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <SVGAssets.Things className="w-5 h-5 text-gray-700" />
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('mediaInfoSidebar.things')}
                  </h3>
                </div>
                <Button
                  onClick={handleOpenEditTags}
                  className="text-gray-500 hover:text-gray-700"
                  btnStyle="no-outline">
                  <SVGAssets.PencilIcon className="w-4 h-4" />
                </Button>
              </div>
              {assetData?.photoTags && assetData.photoTags.length > 0 ? (
                <div className="flex flex-wrap gap-2 ml-7">
                  {assetData.photoTags.map((tag: any, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-lime-200 text-gray-800 text-sm rounded-full">
                      {tag.tagName || t('mediaInfoSidebar.unknown')}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 ml-7">
                  {t('mediaInfoSidebar.noTags')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Tags Modal */}
      <EditTagsModal
        isOpen={isEditTagsModalOpen}
        onClose={handleCloseEditTags}
        assetId={assetId}
        currentTags={currentTags}
        onTagsUpdated={handleTagsUpdated}
      />
    </>
  );
};

export default MediaInfoSidebar;
